function trackFacetAnalytics(eventName, properties) {
  try {
    window.fwdAnalytics?.track?.(eventName, properties);
  } catch (_error) {
    // Section rendering must never depend on analytics.
  }
}

function facetResultCount() {
  const text = document.querySelector('#ProductCount, #ProductCountDesktop')?.textContent || '';
  const match = text.replace(/,/g, '').match(/\d+/);
  return match ? Number(match[0]) : undefined;
}

function facetPublicLabel(element) {
  const label = element?.dataset?.fwdFilterLabel
    || element?.closest?.('label')?.querySelector?.('.facet-checkbox__text-label')?.textContent
    || element?.selectedOptions?.[0]?.textContent
    || '';
  return label.trim().slice(0, 100) || undefined;
}

function canonicalPriceFilterValue(value) {
  let text = String(value ?? '').trim().replace(/[\s']/gu, '');
  if (!text) return '';
  const comma = text.lastIndexOf(',');
  const dot = text.lastIndexOf('.');
  if (comma >= 0 && dot >= 0) {
    const decimalMark = comma > dot ? ',' : '.';
    const groupingMark = decimalMark === ',' ? /\./gu : /,/gu;
    text = text.replace(groupingMark, '').replace(decimalMark, '.');
  } else if (comma >= 0) {
    text = /,\d{1,2}$/u.test(text) ? text.replace(',', '.') : text.replace(/,/gu, '');
  } else if ((text.match(/\./gu) || []).length > 1) {
    const lastDot = text.lastIndexOf('.');
    text = /\.\d{1,2}$/u.test(text)
      ? text.slice(0, lastDot).replace(/\./gu, '') + text.slice(lastDot)
      : text.replace(/\./gu, '');
  }
  if (!/^\d+(?:\.\d{1,2})?$/u.test(text)) return '';
  const number = Number(text);
  if (!Number.isFinite(number) || number < 0 || number > 1000000000000) return '';
  return number.toFixed(2).replace(/\.?0+$/u, '');
}

function facetChange(element, changeType) {
  const details = element?.closest?.('[data-fwd-facet-key]');
  const param = element?.name?.startsWith('filter.') ? element.name : undefined;
  const value = param === 'filter.v.price.gte' || param === 'filter.v.price.lte'
    ? canonicalPriceFilterValue(element?.value)
    : element?.value;
  return {
    eventName: 'Product Filter Changed',
    properties: {
      filter_group: details?.dataset.fwdFacetKey || element?.name,
      filter_param: param,
      filter_value: facetPublicLabel(element) || value,
      filter_value_id: param ? value : undefined,
      change_type: changeType,
    },
  };
}

function analyticsCatalogContext(root) {
  const node = root?.querySelector?.('[data-fwd-analytics-catalog-context]');
  if (!node) return null;
  try {
    return JSON.parse(node.textContent || '{}');
  } catch (_error) {
    return null;
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 300);

    const facetForm = this.querySelector('form');
    this.mobileFacetForm = facetForm?.id === 'FacetFiltersFormMobile' ? facetForm : null;
    if (this.mobileFacetForm) {
      this.mobileCountRequestId = 0;
      this.mobileCountAbortController = null;
      this.mobileCountTarget = this.mobileFacetForm.querySelector('[data-mobile-facets-apply-count]');
      this.mobileCountStatus = this.mobileFacetForm.querySelector('[data-mobile-facets-results-status]');
      this.mobileCommittedCountMarkup = this.mobileCountTarget?.innerHTML || '';
      this.mobileResetRequested = false;
      this.debouncedMobileCountPreview = debounce(() => this.previewMobileResultCount(), 250);
      this.mobileFacetForm.addEventListener('submit', (event) => this.onSubmitHandler(event));
      this.mobileFacetForm.addEventListener('change', (event) => this.onMobileDraftChange(event));
      this.mobileFacetForm.querySelector('[data-mobile-facets-reset]')?.addEventListener('click', () => {
        this.resetMobileDraft();
      });
      this.mobileFacetForm.closest('details.mobile-facets__disclosure')?.addEventListener('toggle', (event) => {
        if (!event.currentTarget.open && !FacetFiltersForm.mobileApplyPending) {
          this.cancelMobileCountPreview();
          this.mobileFacetForm.reset();
          this.updateMobileFacetCounts();
          this.refreshMobileCountElements();
          if (this.mobileCountTarget) this.mobileCountTarget.innerHTML = this.mobileCommittedCountMarkup;
          if (this.mobileCountStatus) this.mobileCountStatus.textContent = '';
          this.mobileResetRequested = false;
        }
      });
    } else {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }
    FacetFiltersForm.reorderFacetGroups();
    FacetFiltersForm.syncStatusControls();
    if (this.mobileFacetForm) {
      this.updateMobileFacetCounts();
      this.mobileAnalyticsBaseline = this.mobileAnalyticsState();
      this.mobileSortBaseline = this.mobileSortValue();
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

  }

  onMobileDraftChange(event) {
    if (event.target.matches('[data-fwd-status-option]') && event.target.checked) {
      this.mobileFacetForm.querySelectorAll('[data-fwd-status-option]').forEach((option) => {
        if (option !== event.target) option.checked = false;
      });
    }
    this.updateMobileFacetCounts();
    this.debouncedMobileCountPreview();
  }

  updateMobileFacetCounts() {
    this.mobileFacetForm.querySelectorAll('details[data-fwd-facet-key]:not([data-fwd-facet-key="sort"])').forEach((details) => {
      const countTarget = details.querySelector(':scope > summary [data-mobile-facet-count]');
      if (!countTarget) return;

      const facetKey = details.dataset.fwdFacetKey;
      let selectedCount = 0;
      if (facetKey === 'category') {
        const category = details.querySelector('[data-fwd-category-url]:checked:not(:disabled)');
        selectedCount = category && category.value !== 'all' ? 1 : 0;
      } else {
        const checkedInputs = details.querySelectorAll('input:checked:not(:disabled)');
        selectedCount = checkedInputs.length;
        if (!selectedCount) {
          selectedCount = Array.from(details.querySelectorAll('input[type="number"]:not(:disabled)'))
            .some((input) => input.value.trim() !== '') ? 1 : 0;
        }
      }

      countTarget.textContent = `(${selectedCount})`;
      countTarget.hidden = selectedCount === 0;
    });
  }

  resetMobileDraft() {
    this.mobileResetRequested = true;
    this.mobileFacetForm.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.checked = false;
    });
    this.mobileFacetForm.querySelector('input[type="radio"][value="all"]')?.click();
    this.mobileFacetForm.querySelectorAll('input[type="number"]').forEach((input) => {
      input.value = '';
    });
    const sort = this.mobileFacetForm.querySelector('input[type="radio"][name="sort_by"]');
    if (sort) sort.checked = true;
    this.updateMobileFacetCounts();
    this.debouncedMobileCountPreview();
  }

  mobileAnalyticsState() {
    const state = new Map();
    if (!this.mobileFacetForm) return state;
    Array.from(this.mobileFacetForm.elements).forEach((element) => {
      if (!element.name || element.disabled || (!element.name.startsWith('filter.') && element.name !== 'fwd_category')) return;
      if ((element.type === 'checkbox' || element.type === 'radio') && !element.checked) return;
      if (element.type === 'number' && !element.value.trim()) return;
      if (element.name === 'fwd_category' && element.value === 'all') return;
      const value = element.name === 'filter.v.price.gte' || element.name === 'filter.v.price.lte'
        ? canonicalPriceFilterValue(element.value)
        : element.value;
      if (!value) return;
      const key = `${element.name}\u0000${value}`;
      state.set(key, {
        name: element.name,
        filter_group: element.closest?.('[data-fwd-facet-key]')?.dataset.fwdFacetKey || element.name,
        filter_param: element.name.startsWith('filter.') ? element.name : undefined,
        filter_value: facetPublicLabel(element) || value,
        filter_value_id: value,
        single_choice: element.type === 'radio' || element.closest?.('[data-fwd-facet-key]')?.dataset.fwdFacetKey === 'status',
      });
    });
    return state;
  }

  mobileSortValue() {
    return this.mobileFacetForm?.querySelector('input[name="sort_by"]:checked, select[name="sort_by"]')?.value || '';
  }

  trackMobileFacetChanges() {
    const current = this.mobileAnalyticsState();
    const sortValue = this.mobileSortValue();
    const events = [];
    if (this.mobileResetRequested) {
      events.push({
        eventName: 'Product Filter Changed',
        properties: { filter_group: 'all', filter_value: 'reset', change_type: 'reset' },
      });
    } else {
      const changes = [];
      const replacedBaselineKeys = new Set();
      current.forEach((value, key) => {
        if (this.mobileAnalyticsBaseline.has(key)) return;
        const replaced = Array.from(this.mobileAnalyticsBaseline.entries())
          .filter(([baselineKey, baseline]) => baseline.name === value.name && baseline.single_choice && baselineKey !== key);
        replaced.forEach(([baselineKey]) => replacedBaselineKeys.add(baselineKey));
        changes.push(Object.assign({ change_type: 'apply' }, value));
      });
      this.mobileAnalyticsBaseline.forEach((value, key) => {
        if (!current.has(key) && !replacedBaselineKeys.has(key)) changes.push(Object.assign({ change_type: 'remove' }, value));
      });
      changes
        .sort((left, right) => `${left.filter_group}\u0000${left.filter_value}`.localeCompare(`${right.filter_group}\u0000${right.filter_value}`))
        .forEach((change) => events.push({
          eventName: 'Product Filter Changed',
          properties: {
            filter_group: change.filter_group,
            filter_param: change.filter_param,
            filter_value: change.filter_value,
            filter_value_id: change.filter_value_id,
            change_type: change.change_type,
          },
        }));
    }
    if (sortValue && sortValue !== this.mobileSortBaseline) {
      events.push({ eventName: 'Product Sort Changed', properties: { sort_value: sortValue } });
    }
    this.mobilePendingAnalyticsState = { current, sortValue };
    return events;
  }

  commitMobileAnalyticsState() {
    if (!this.mobilePendingAnalyticsState) return;
    this.mobileAnalyticsBaseline = this.mobilePendingAnalyticsState.current;
    this.mobileSortBaseline = this.mobilePendingAnalyticsState.sortValue;
    this.mobilePendingAnalyticsState = null;
    this.mobileResetRequested = false;
  }

  cancelMobileCountPreview() {
    this.mobileCountRequestId += 1;
    this.mobileCountAbortController?.abort();
    this.mobileCountAbortController = null;
    const applyButton = this.mobileFacetForm?.querySelector('[data-mobile-facets-results]');
    applyButton?.removeAttribute('aria-busy');
  }

  refreshMobileCountElements() {
    this.mobileCountTarget = this.mobileFacetForm?.querySelector('[data-mobile-facets-apply-count]') || null;
    this.mobileCountStatus = this.mobileFacetForm?.querySelector('[data-mobile-facets-results-status]') || null;
  }

  async previewMobileResultCount() {
    if (!this.mobileFacetForm) return;
    this.refreshMobileCountElements();
    if (!this.mobileCountTarget) return;

    const sectionId = document.getElementById('product-grid')?.dataset.id;
    if (!sectionId) return;

    this.cancelMobileCountPreview();
    const requestId = this.mobileCountRequestId;
    this.mobileCountAbortController = new AbortController();
    const applyButton = this.mobileFacetForm.querySelector('[data-mobile-facets-results]');
    applyButton?.setAttribute('aria-busy', 'true');
    this.mobileCountTarget.textContent = this.mobileCountTarget.dataset.loadingLabel || '…';
    if (this.mobileCountStatus) this.mobileCountStatus.textContent = '';

    const category = this.mobileFacetForm.querySelector('[data-fwd-category-url]:checked');
    const baseUrl = category?.dataset.fwdCategoryUrl || this.mobileFacetForm.dataset.fwdCollectionUrl || window.location.href;
    const pathname = new URL(baseUrl, window.location.origin).pathname;
    const searchParams = this.createSearchParams(this.mobileFacetForm);
    const requestUrl = `${pathname}?section_id=${encodeURIComponent(sectionId)}&${searchParams}`;

    try {
      const response = await fetch(requestUrl, { signal: this.mobileCountAbortController.signal });
      if (!response.ok) throw new Error(`Filter count request failed: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const countSource = html.getElementById('ProductCount');
      if (!countSource) throw new Error('Filter count response is missing ProductCount');
      if (requestId !== this.mobileCountRequestId) return;
      this.mobileCountTarget.innerHTML = countSource.innerHTML;
      if (this.mobileCountStatus) this.mobileCountStatus.textContent = countSource.textContent.trim();
    } catch (error) {
      if (error.name === 'AbortError' || requestId !== this.mobileCountRequestId) return;
      this.mobileCountTarget.textContent = this.mobileCountTarget.dataset.errorLabel || '—';
      if (this.mobileCountStatus) {
        this.mobileCountStatus.textContent = this.mobileCountStatus.dataset.errorMessage || '';
      }
    } finally {
      if (requestId === this.mobileCountRequestId) {
        applyButton?.removeAttribute('aria-busy');
        this.mobileCountAbortController = null;
      }
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static reorderFacetGroups(root = document) {
    const priority = {
      sort: 0,
      category: 10,
      brand: 20,
      'tagged-size': 30,
      'recommended-size': 40,
      'production-era': 50,
      status: 60,
      inventory: 70,
      other: 100,
    };

    root.querySelectorAll('#FacetsWrapperDesktop, #FacetsWrapperMobile').forEach((container) => {
      const groups = Array.from(container.children).filter((element) =>
        element.matches('details[data-fwd-facet-key]')
      );
      if (groups.length < 2) return;

      const anchor = groups[groups.length - 1].nextSibling;
      const fragment = document.createDocumentFragment();
      groups
        .map((element, index) => ({ element, index }))
        .sort((left, right) => {
          const leftPriority = priority[left.element.dataset.fwdFacetKey] ?? priority.other;
          const rightPriority = priority[right.element.dataset.fwdFacetKey] ?? priority.other;
          return leftPriority - rightPriority || left.index - right.index;
        })
        .forEach(({ element }) => fragment.appendChild(element));
      container.insertBefore(fragment, anchor);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.requestId += 1;
    const requestId = FacetFiltersForm.requestId;
    FacetFiltersForm.analyticsByRequest.set(requestId, FacetFiltersForm.pendingAnalytics.splice(0));
    if (FacetFiltersForm.abortController) FacetFiltersForm.abortController.abort();
    FacetFiltersForm.abortController = new AbortController();
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, requestId)
        : FacetFiltersForm.renderSectionFromFetch(url, event, requestId);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, requestId) {
    fetch(url, { signal: FacetFiltersForm.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (requestId !== FacetFiltersForm.requestId) return;
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.commitAnalytics(requestId, html);
        FacetFiltersForm.finishMobileApply();
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          FacetFiltersForm.cancelMobileApply();
          console.error('Unable to update product filters.', error);
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event, requestId) {
    if (requestId !== FacetFiltersForm.requestId) return;
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.commitAnalytics(requestId, html);
    FacetFiltersForm.finishMobileApply();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    document.dispatchEvent(
      new CustomEvent('collection:product-grid:rendered', {
        detail: {
          containerId: 'ProductGridContainer',
        },
      })
    );
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const countSource = parsedHTML.getElementById('ProductCount');
    const count = countSource.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.closest('.product-count')?.toggleAttribute(
      'hidden',
      countSource.closest('.product-count')?.hasAttribute('hidden') ?? false
    );
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    FwdSearchQuery.hydrate(document);

    if (FacetFiltersForm.restoreFocusAfterRemove) {
      const visibleCount = window.matchMedia('(min-width: 990px)').matches ? containerDesktop : container;
      const focusTarget = document.querySelector('[data-active-filters-bar] a') || visibleCount || container;
      if (focusTarget) {
        if (!focusTarget.matches('a, button, input, select, textarea, [tabindex]')) {
          focusTarget.setAttribute('tabindex', '-1');
        }
        focusTarget.focus();
      }
      FacetFiltersForm.restoreFocusAfterRemove = false;
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.reorderFacetGroups();

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    FacetFiltersForm.syncStatusControls(FacetFiltersForm.searchParamsPrev);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const activeFacetsTarget = document.querySelector(selector);
      if (!activeFacetsElement || !activeFacetsTarget) return;
      activeFacetsTarget.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
    ];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });

    const resultsButton = html.querySelector('[data-mobile-facets-results]');
    if (resultsButton) {
      document.querySelectorAll('[data-mobile-facets-results]').forEach((button) => {
        button.innerHTML = resultsButton.innerHTML;
      });
    }

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm) {
      const filtersForm = mobileForm.closest('facet-filters-form');
      filtersForm?.refreshMobileCountElements();
      mobileForm.closest('menu-drawer').bindEvents();
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static inventoryRemovalUrl(source = window.location.href, replacementSearchParams = null) {
    const destination = new URL(source, window.location.origin);
    if (replacementSearchParams !== null) {
      destination.search = String(replacementSearchParams) ? `?${replacementSearchParams}` : '';
    }
    destination.searchParams.delete('filter.v.availability');
    destination.searchParams.set('fwd_inventory', 'all');
    destination.searchParams.delete('page');
    return destination;
  }

  static queueAnalytics(events) {
    FacetFiltersForm.pendingAnalytics = Array.isArray(events) ? events.filter(Boolean) : [];
  }

  static commitAnalytics(requestId, html) {
    const events = FacetFiltersForm.analyticsByRequest.get(requestId) || [];
    FacetFiltersForm.analyticsByRequest.delete(requestId);
    if (requestId !== FacetFiltersForm.requestId) return;
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const catalog = analyticsCatalogContext(parsedHTML);
    if (catalog) {
      FacetFiltersForm.analyticsCatalog = catalog;
      try {
        window.fwdAnalytics?.setCatalogContext?.(catalog, {
          applied: true, requestId, resultCount: facetResultCount(),
          filterChanged: events.some((event) => event.eventName === 'Product Filter Changed'),
          changeType: events.length === 1 ? events[0].properties.change_type : 'batch',
        });
      } catch (_error) {
        // A catalog context update must never affect Section Rendering.
      }
    }
    if (!events.length) return;
    const resultCount = facetResultCount();
    events.forEach((event) => {
      trackFacetAnalytics(event.eventName, Object.assign({}, event.properties, {
        result_count: resultCount,
      }));
    });
  }

  static commitNavigationAnalytics(updateCatalog) {
    const events = FacetFiltersForm.pendingAnalytics.splice(0);
    if (!events.length) return;
    let catalog = FacetFiltersForm.currentAnalyticsCatalog();
    if (catalog && typeof updateCatalog === 'function') catalog = updateCatalog(catalog) || catalog;
    if (catalog) {
      FacetFiltersForm.analyticsCatalog = catalog;
      try {
        window.fwdAnalytics?.setCatalogContext?.(catalog);
      } catch (_error) {
        // Navigation must not depend on analytics context.
      }
    }
    events.forEach((event) => trackFacetAnalytics(event.eventName, event.properties));
  }

  static currentAnalyticsCatalog() {
    const catalog = FacetFiltersForm.analyticsCatalog || analyticsCatalogContext(document);
    if (!catalog) return null;
    try {
      return JSON.parse(JSON.stringify(catalog));
    } catch (_error) {
      return null;
    }
  }

  static catalogForNavigationUrl(catalog, destination) {
    if (!catalog) return null;
    let targetUrl;
    try {
      targetUrl = destination instanceof URL
        ? destination
        : new URL(destination, window.location.origin);
    } catch (_error) {
      return null;
    }
    const pathSegments = targetUrl.pathname.split('/').filter(Boolean);
    const collectionIndex = pathSegments.indexOf('collections');
    catalog.collectionHandle = collectionIndex >= 0 ? pathSegments[collectionIndex + 1] || null : null;
    const routeStatus = FacetFiltersForm.statusFromPath(targetUrl.pathname);
    catalog.activeFilters = (catalog.activeFilters || []).filter((filter) => {
      if (!filter || typeof filter.param !== 'string') return false;
      if (filter.param === 'filter.p.tag' && routeStatus === String(filter.value)) return true;
      return targetUrl.searchParams.getAll(filter.param).some((rawValue) =>
        rawValue.split(',').some((value) => value === String(filter.value))
      );
    });
    const brandFilters = catalog.activeFilters.filter((filter) => filter.param === 'filter.p.m.custom.brand');
    if (brandFilters.length !== 1 || String(catalog.brand?.brandId || '') !== String(brandFilters[0].value)) {
      catalog.brand = null;
    }
    return catalog;
  }

  static finishMobileApply() {
    if (!FacetFiltersForm.mobileApplyPending) return;
    const form = document.getElementById('FacetFiltersFormMobile');
    form?.querySelectorAll('input').forEach((input) => {
      if (input.type === 'checkbox' || input.type === 'radio') input.defaultChecked = input.checked;
      else input.defaultValue = input.value;
    });
    form?.querySelectorAll('select option').forEach((option) => {
      option.defaultSelected = option.selected;
    });
    const disclosure = form?.closest('details.mobile-facets__disclosure');
    const drawer = disclosure?.closest('menu-drawer');
    const summary = disclosure?.querySelector(':scope > summary');
    if (disclosure?.open && drawer?.closeMenuDrawer) {
      drawer.closeMenuDrawer(new Event('mobile-facets-apply'), summary);
    }
    if (drawer?.dataset.breakpoint) {
      document.body.classList.remove(`overflow-hidden-${drawer.dataset.breakpoint}`);
    }
    form?.removeAttribute('aria-busy');
    const applyButton = form?.querySelector('[data-mobile-facets-results]');
    if (applyButton) applyButton.disabled = false;
    const formElement = form?.closest('facet-filters-form');
    const appliedCount = form?.querySelector('[data-mobile-facets-apply-count]');
    if (formElement && appliedCount) formElement.mobileCommittedCountMarkup = appliedCount.innerHTML;
    formElement?.commitMobileAnalyticsState?.();
    FacetFiltersForm.mobileApplyPending = false;
  }

  static cancelMobileApply() {
    const form = document.getElementById('FacetFiltersFormMobile');
    form?.removeAttribute('aria-busy');
    const applyButton = form?.querySelector('[data-mobile-facets-results]');
    if (applyButton) applyButton.disabled = false;
    FacetFiltersForm.mobileApplyPending = false;
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);
    ['filter.v.price.gte', 'filter.v.price.lte'].forEach((param) => {
      const value = canonicalPriceFilterValue(searchParams.get(param));
      searchParams.delete(param);
      if (value) searchParams.set(param, value);
    });
    searchParams.delete('fwd_sort_choice');
    searchParams.delete('fwd_category');
    const activeStatuses = searchParams
      .getAll('filter.p.tag')
      .filter((value) => FacetFiltersForm.statusValues.includes(value));
    const inStockOnly = searchParams.getAll('filter.v.availability').includes('1');

    searchParams.delete('filter.p.tag');
    searchParams.delete('filter.v.availability');
    if (activeStatuses.length) {
      searchParams.append('filter.p.tag', activeStatuses[activeStatuses.length - 1]);
    }
    if (inStockOnly) searchParams.append('filter.v.availability', '1');
    if (form.querySelector('[data-fwd-in-stock-only]')) {
      if (inStockOnly) searchParams.delete('fwd_inventory');
      else searchParams.set('fwd_inventory', 'all');
    } else if (new URL(window.location.href).searchParams.get('fwd_inventory') === 'all') {
      searchParams.set('fwd_inventory', 'all');
    }

    if (!form.dataset.fwdCollectionUrl && searchParams.has('q')) {
      const displayQuery = FwdSearchQuery.readDisplayQuery();
      FwdSearchQuery.applyStorefrontContract(searchParams, displayQuery);
    }

    return searchParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sourceForm = event.target.closest('form');
    const isMobileFilter = sourceForm?.id === 'FacetFiltersFormMobile';
    const isMobileQuickSort = sourceForm?.id === 'FacetSortMobileToolbarForm';
    const changed = event.target;
    const isSort = changed?.name === 'sort_by' || changed?.name === 'fwd_sort_choice';
    let analyticsEvents = [];
    if (isMobileFilter) {
      analyticsEvents = this.trackMobileFacetChanges();
    } else if (isSort) {
      analyticsEvents = [{ eventName: 'Product Sort Changed', properties: { sort_value: changed.value } }];
    } else {
      analyticsEvents = [facetChange(changed, changed?.checked === false ? 'remove' : 'apply')];
    }
    FacetFiltersForm.queueAnalytics(analyticsEvents);
    if (isMobileQuickSort) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('sort_by', event.target.value);
      searchParams.delete('page');
      sourceForm.closest('details')?.removeAttribute('open');
      this.onSubmitForm(searchParams.toString(), event);
      return;
    }
    if (isMobileFilter) {
      sourceForm.closest('facet-filters-form')?.cancelMobileCountPreview();
      const category = sourceForm.querySelector('[data-fwd-category-url]:checked');
      if (category) {
        const targetUrl = new URL(category.dataset.fwdCategoryUrl, window.location.origin);
        if (targetUrl.pathname !== window.location.pathname) {
          history.replaceState(history.state, '', `${targetUrl.pathname}${window.location.search}`);
        }
      } else if (sourceForm.dataset.fwdCollectionUrl) {
        this.useMobileCollectionBase(sourceForm);
      }
      FacetFiltersForm.mobileApplyPending = true;
      sourceForm.setAttribute('aria-busy', 'true');
      const applyButton = sourceForm.querySelector('[data-mobile-facets-results]');
      if (applyButton) applyButton.disabled = true;
    }
    if (event.target.matches('[data-fwd-status-option]') && event.target.checked) {
      const form = event.target.closest('form');
      form
        .querySelectorAll('[data-fwd-status-option]')
        .forEach((option) => {
          if (option !== event.target) option.checked = false;
        });
    }
    if (!isMobileFilter && event.target.matches('[data-fwd-status-option]') && event.target.form.dataset.fwdCollectionUrl) {
      const statusOption = event.target.checked ? event.target : null;
      FacetFiltersForm.commitNavigationAnalytics((catalog) => {
        const filters = (catalog.activeFilters || []).filter((filter) => filter.param !== 'filter.p.tag');
        if (statusOption && FacetFiltersForm.statusValues.includes(statusOption.value)) {
          filters.push({ param: 'filter.p.tag', value: statusOption.value, label: facetPublicLabel(statusOption) || statusOption.value });
        }
        catalog.activeFilters = filters;
        return catalog;
      });
      this.navigateCollectionStatus(event.target.form);
      return;
    }
    const currentInventoryOnly = new URL(window.location.href).searchParams
      .getAll('filter.v.availability')
      .includes('1');
    const mobileInventoryRemoved = isMobileFilter
      && currentInventoryOnly
      && !sourceForm.querySelector('[data-fwd-in-stock-only]:checked');
    const desktopInventoryRemoved = !isMobileFilter
      && changed.matches('[data-fwd-in-stock-only]')
      && !changed.checked;
    if (mobileInventoryRemoved || desktopInventoryRemoved) {
      const destination = FacetFiltersForm.inventoryRemovalUrl(
        window.location.href,
        isMobileFilter ? this.createSearchParams(sourceForm) : null
      );
      FacetFiltersForm.commitNavigationAnalytics((catalog) => {
        return FacetFiltersForm.catalogForNavigationUrl(catalog, destination);
      });
      window.location.assign(destination);
      return;
    }
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (!isMobileFilter && event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      sortFilterForms.forEach((form) => {
        if (!isMobileFilter) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  useMobileCollectionBase(form) {
    const baseUrl = new URL(form.dataset.fwdCollectionUrl, window.location.origin);
    if (baseUrl.pathname === window.location.pathname) return;

    const currentUrl = new URL(window.location.href);
    currentUrl.pathname = baseUrl.pathname;
    history.replaceState(history.state, '', currentUrl);
  }

  navigateCollectionStatus(form) {
    const targetUrl = new URL(form.dataset.fwdCollectionUrl, window.location.origin);
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.forEach((value, key) => {
      if (key !== 'filter.v.availability' && key !== 'filter.p.tag' && key !== 'page') {
        targetUrl.searchParams.append(key, value);
      }
    });

    const activeStatus = Array.from(form.querySelectorAll('[data-fwd-status-option]:checked'))
      .map((option) => option.value)
      .find((value) => FacetFiltersForm.statusValues.includes(value));
    if (activeStatus) {
      if (activeStatus === 'new' && targetUrl.pathname === '/collections/all') {
        targetUrl.pathname = '/collections/new';
      } else {
        targetUrl.pathname = `${targetUrl.pathname.replace(/\/$/u, '')}/${activeStatus}`;
      }
    }
    if (form.querySelector('[data-fwd-in-stock-only]:checked')) {
      targetUrl.searchParams.set('filter.v.availability', '1');
    }
    window.location.assign(targetUrl);
  }

  static statusFromPath(pathname = window.location.pathname) {
    return pathname.split('/').find((value) => FacetFiltersForm.statusValues.includes(value));
  }

  static syncStatusControls(searchParams = window.location.search.slice(1)) {
    const params = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams);
    const activeStatus = params
      .getAll('filter.p.tag')
      .find((value) => FacetFiltersForm.statusValues.includes(value))
      || FacetFiltersForm.statusFromPath();
    const inStockOnly = params.getAll('filter.v.availability').includes('1')
      && !params.getAll('filter.v.availability').includes('0');

    document.querySelectorAll('[data-fwd-status-option]').forEach((option) => {
      option.checked = option.value === activeStatus;
      option.closest('label')?.classList.toggle('active', option.checked);
    });
    document.querySelectorAll('[data-fwd-in-stock-only]').forEach((option) => {
      option.checked = inStockOnly;
      option.closest('label')?.classList.toggle('active', option.checked);
    });
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const link = event.currentTarget;
    const isReset = link?.classList?.contains('fwd-active-filters-bar__clear')
      || link?.classList?.contains('active-facets__button-remove');
    FacetFiltersForm.queueAnalytics([{
      eventName: 'Product Filter Changed',
      properties: {
        filter_group: isReset ? 'all' : link?.dataset?.filterParam || (link?.matches?.('[data-fwd-remove-category]') ? 'category' : 'status'),
        filter_param: link?.dataset?.filterParam,
        filter_value: isReset ? 'reset' : facetPublicLabel(link),
        filter_value_id: link?.dataset?.filterValue,
        change_type: isReset ? 'reset' : 'remove',
      },
    }]);
    if (event.currentTarget.matches('[data-fwd-remove-category]')) {
      const destination = this.categoryRemovalUrl(link);
      FacetFiltersForm.commitNavigationAnalytics((catalog) => {
        return FacetFiltersForm.catalogForNavigationUrl(catalog, destination);
      });
      this.removeCategoryFilter(destination);
      return;
    }
    if (event.currentTarget.matches('[data-fwd-remove-status]')) {
      FacetFiltersForm.commitNavigationAnalytics((catalog) => {
        catalog.activeFilters = (catalog.activeFilters || []).filter((filter) => filter.param !== 'filter.p.tag');
        return catalog;
      });
      this.removeStatusFilter();
      return;
    }
    if (link?.dataset?.filterParam === 'filter.v.availability') {
      const destination = FacetFiltersForm.inventoryRemovalUrl(link.href);
      FacetFiltersForm.commitNavigationAnalytics((catalog) => {
        return FacetFiltersForm.catalogForNavigationUrl(catalog, destination);
      });
      window.location.assign(destination);
      return;
    }
    FacetFiltersForm.restoreFocusAfterRemove = true;
    FacetFiltersForm.toggleActiveFacets();
    const queryString =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    const searchParams = new URLSearchParams(queryString);
    if (isReset) searchParams.delete('filter.v.availability');
    if (isReset || new URL(window.location.href).searchParams.get('fwd_inventory') === 'all') {
      searchParams.set('fwd_inventory', 'all');
    }
    if (!document.querySelector('[data-fwd-collection-url]') && searchParams.has('q')) {
      FwdSearchQuery.applyStorefrontContract(searchParams, FwdSearchQuery.readDisplayQuery());
    }
    FacetFiltersForm.renderPage(searchParams.toString());
  }

  removeStatusFilter() {
    const targetUrl = new URL(window.location.href);
    if (targetUrl.pathname === '/collections/new') {
      targetUrl.pathname = '/collections/all';
    } else {
      targetUrl.pathname = targetUrl.pathname.replace(/\/(?:new|store-only|coming-soon)\/?$/u, '');
    }
    targetUrl.searchParams.delete('filter.p.tag');
    targetUrl.searchParams.delete('page');

    window.location.assign(targetUrl);
  }

  categoryRemovalUrl(link) {
    const currentUrl = new URL(window.location.href);
    const targetUrl = new URL(link.href, window.location.origin);
    const activeStatus = FacetFiltersForm.statusFromPath(currentUrl.pathname);
    targetUrl.pathname = `${targetUrl.pathname.replace(/\/$/u, '')}${activeStatus ? `/${activeStatus}` : ''}`;
    const currentSort = currentUrl.searchParams.get('sort_by');
    if (currentSort) targetUrl.searchParams.set('sort_by', currentSort);
    if (currentUrl.searchParams.get('fwd_inventory') === 'all') {
      targetUrl.searchParams.delete('filter.v.availability');
      targetUrl.searchParams.set('fwd_inventory', 'all');
    }
    targetUrl.searchParams.delete('page');
    return targetUrl;
  }

  removeCategoryFilter(destination) {
    window.location.assign(destination);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.abortController = null;
FacetFiltersForm.analyticsByRequest = new Map();
FacetFiltersForm.analyticsCatalog = analyticsCatalogContext(document);
FacetFiltersForm.pendingAnalytics = [];
FacetFiltersForm.requestId = 0;
FacetFiltersForm.restoreFocusAfterRemove = false;
FacetFiltersForm.statusValues = ['new', 'store-only', 'coming-soon'];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

if (!window.fwdCategoryFilterInitialized) {
  window.fwdCategoryFilterInitialized = true;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-category-filter-link]');
    if (!link) return;

    event.preventDefault();
    const targetUrl = new URL(link.href, window.location.origin);
    const currentUrl = new URL(window.location.href);
    const currentSort = currentUrl.searchParams.get('sort_by');
    if (currentSort) targetUrl.searchParams.set('sort_by', currentSort);
    if (currentUrl.searchParams.get('fwd_inventory') === 'all') {
      targetUrl.searchParams.delete('filter.v.availability');
      targetUrl.searchParams.set('fwd_inventory', 'all');
    }
    FacetFiltersForm.queueAnalytics([{
      eventName: 'Product Filter Changed',
      properties: {
        filter_group: 'category',
        filter_value: link.textContent?.trim().slice(0, 100),
        change_type: 'apply',
      },
    }]);
    FacetFiltersForm.commitNavigationAnalytics((catalog) =>
      FacetFiltersForm.catalogForNavigationUrl(catalog, targetUrl)
    );
    window.location.assign(targetUrl);
  });
}
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class FwdSortControl extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select[name="sort_by"]');
    this.trigger = this.querySelector('.fwd-sort-control__trigger');
    this.popoverElement = this.querySelector('.fwd-sort-control__popover');
    this.current = this.querySelector('[data-fwd-sort-current]');
    this.radios = [...this.querySelectorAll('input[type="radio"][name="fwd_sort_choice"]')];
    if (!this.select || !this.trigger || !this.popoverElement || !this.current || !this.radios.length) return;

    this.onTriggerClick = () => this.toggle();
    this.onTriggerKeydown = (event) => {
      if (event.key !== 'ArrowDown') return;
      event.preventDefault();
      this.open(true);
    };
    this.onPopoverKeydown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.close(true);
    };
    this.onDocumentPointerdown = (event) => {
      if (!this.contains(event.target)) this.close(false);
    };

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.trigger.addEventListener('keydown', this.onTriggerKeydown);
    this.popoverElement.addEventListener('keydown', this.onPopoverKeydown);
    this.radios.forEach((radio) => {
      radio.addEventListener('input', (event) => event.stopPropagation());
      radio.addEventListener('change', (event) => this.selectOption(event));
    });
    document.addEventListener('pointerdown', this.onDocumentPointerdown);
    this.select.tabIndex = -1;
    this.select.setAttribute('aria-hidden', 'true');
    this.classList.add('is-enhanced');
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.onTriggerClick);
    this.trigger?.removeEventListener('keydown', this.onTriggerKeydown);
    this.popoverElement?.removeEventListener('keydown', this.onPopoverKeydown);
    document.removeEventListener('pointerdown', this.onDocumentPointerdown);
  }

  open(focusSelected = false) {
    this.popoverElement.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    if (focusSelected) (this.radios.find((radio) => radio.checked) || this.radios[0])?.focus();
  }

  close(restoreFocus = false) {
    if (this.popoverElement.hidden) return;
    this.popoverElement.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.trigger.focus();
  }

  toggle() {
    if (this.popoverElement.hidden) this.open(false);
    else this.close(false);
  }

  selectOption(event) {
    event.stopPropagation();
    const radio = event.currentTarget;
    const option = [...this.select.options].find((item) => item.value === radio.value);
    if (!option) return;
    this.select.value = radio.value;
    this.current.textContent = option.textContent.trim();
    this.close(true);
    this.select.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

if (!customElements.get('fwd-sort-control')) {
  customElements.define('fwd-sort-control', FwdSortControl);
}

class MobileFacetAffordance {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.anchor = wrapper.previousElementSibling?.matches('[data-mobile-filter-anchor]')
      ? wrapper.previousElementSibling
      : null;
    if (!this.anchor || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(([entry]) => {
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      const hasScrolledPastAnchor = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.wrapper.classList.toggle('mobile-facets__wrapper--floating', isMobile && hasScrolledPastAnchor);
    });
    this.observer.observe(this.anchor);
  }
}

document
  .querySelectorAll('.mobile-facets__wrapper')
  .forEach((wrapper) => new MobileFacetAffordance(wrapper));

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form) form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
